import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { textareaFrameClass, type FieldSize } from "../../lib/field-frame.js";
import { measureAutoResize } from "../../lib/textarea-autoresize.js";
import { mergeRefs } from "../../primitives/index.js";
import { announce } from "../../primitives/announce.js";
import { TextareaMetaRow } from "./TextareaMetaRow.js";

/* ---------------- Textarea ----------------
   `variant` / `size` are the field look (lib/field-frame.ts). The default
   always renders the meta row under the field; ghost renders it only when
   there is a hint, an error or a count, so a borderless editor leaves no
   empty gap (TextareaMetaRow). `variant="readonly"` also sets native
   `readOnly`; `readOnly` alone keeps the default look. `variant="code"` is
   the default frame plus font-mono text-xs leading-5 — its own axis, so it
   is exclusive with ghost/readonly by type, never a class override.

   `error` replaces the hint with the message, sets `aria-invalid` and the
   red border — the same contract as Input's `error`. The hint (or error)
   paragraph always carries an id that the textarea's `aria-describedby`
   points at, in every variant/size — that link previously did not exist.
   `hintLive` puts `aria-live="polite"` on that paragraph, but only while it
   is showing the hint (not the error); a default hint stays silent.

   `countLimit` is a SOFT cap: a "n / limit" counter with no native
   `maxLength`, so pasting past it keeps every character. Crossing it turns
   the counter the watch tone (`lib/tones.ts` — the limit is soft, unlike a
   host's own submit error) and announces "N over the limit" through the
   shared `announce()` region once, on the change that crosses — never again
   while it stays over, which is what would flood a screen reader.

   `autoResize` grows the field from its `rows` to fit content (resize-none,
   no inner scroll) and shrinks as content is removed; `maxRows` caps the
   growth and turns on vertical scrolling. `lib/textarea-autoresize.ts` does
   the measuring, re-run on value change and on the element's own width
   changing (a ResizeObserver) — never on every render, which is what would
   thrash a long list of these fields. */

interface TextareaOwnProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  showCount?: boolean;
  countLimit?: number;
  hintLive?: boolean;
  wrapperClassName?: string;
  autoResize?: boolean;
  maxRows?: number;
}

/** Same two axes as `FieldLookProps` (`lib/field-frame.ts`), but the
 *  non-ghost branch also takes `"code"` — Textarea-only, so the shared type
 *  Input and NumberInput use stays untouched. */
type TextareaFieldLookProps =
  | {
      variant?: "default" | "readonly" | "code" | undefined;
      size?: "sm" | "md" | undefined;
    }
  | {
      variant: "ghost";
      size?: FieldSize | undefined;
    };

export type TextareaProps = TextareaOwnProps & TextareaFieldLookProps;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      hint,
      error,
      showCount,
      countLimit,
      hintLive,
      wrapperClassName,
      variant,
      size,
      className,
      id,
      readOnly,
      maxLength,
      value,
      defaultValue,
      onChange,
      autoResize,
      maxRows,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedBy,
      ...rest
    },
    ref,
  ) {
    const innerRef = React.useRef<HTMLTextAreaElement>(null);
    const autoId = React.useId();
    const taId = id ?? autoId;
    const hasError = !!error;
    const invalid = hasError || ariaInvalid === true || ariaInvalid === "true";
    const isControlled = value !== undefined;
    const [internalLen, setInternalLen] = React.useState(
      typeof defaultValue === "string" ? defaultValue.length : 0,
    );
    const len = isControlled
      ? typeof value === "string"
        ? value.length
        : 0
      : internalLen;

    const hasCountLimit = countLimit !== undefined;
    const overLimit = hasCountLimit && len > countLimit;
    const wasOverLimitRef = React.useRef(overLimit);
    React.useEffect(() => {
      if (overLimit && !wasOverLimitRef.current) {
        announce(`${len - countLimit!} over the limit`, {
          politeness: "polite",
        });
      }
      wasOverLimitRef.current = overLimit;
    }, [overLimit, len, countLimit]);

    const message = hasError ? error : hint;
    const messageId = message ? `${taId}-description` : undefined;
    const describedBy =
      [ariaDescribedBy, messageId].filter(Boolean).join(" ") || undefined;

    // Re-measure on value change (controlled or typed) — never on every
    // render, which is what would thrash a long list of these fields.
    const measureTrigger = isControlled ? value : internalLen;
    React.useLayoutEffect(() => {
      if (!autoResize) return;
      const el = innerRef.current;
      if (!el) return;
      measureAutoResize(el, maxRows);
    }, [autoResize, maxRows, measureTrigger]);

    // A narrower/wider field wraps text differently at the same content, so
    // also re-measure when the element's own width changes.
    React.useEffect(() => {
      if (!autoResize) return undefined;
      const el = innerRef.current;
      if (!el || typeof ResizeObserver === "undefined") return undefined;
      let width = el.clientWidth;
      const observer = new ResizeObserver(() => {
        if (el.clientWidth === width) return;
        width = el.clientWidth;
        measureAutoResize(el, maxRows);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [autoResize, maxRows]);

    return (
      <div className={cn("w-full", wrapperClassName)}>
        {label && (
          <label
            htmlFor={taId}
            className="block text-xs font-medium text-pb-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea
          ref={mergeRefs(innerRef, ref)}
          id={taId}
          readOnly={variant === "readonly" || readOnly}
          value={value}
          defaultValue={defaultValue}
          maxLength={maxLength}
          onChange={(e) => {
            if (!isControlled) setInternalLen(e.target.value.length);
            onChange?.(e);
          }}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            textareaFrameClass({ variant, size, invalid, autoResize }),
            className,
          )}
          {...rest}
        />
        <TextareaMetaRow
          ghost={variant === "ghost"}
          message={message}
          messageId={messageId}
          hasError={hasError}
          hintLive={hintLive}
          showCount={showCount}
          len={len}
          countLimit={countLimit}
          maxLength={maxLength}
        />
      </div>
    );
  },
);
